import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Box,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useUnifiedBreadcrumbs } from '../../hooks/useUnifiedBreadcrumbs';
import { usePageActions } from '../../contexts/PageActionsContext';
import { BreadcrumbItem } from './types';
import { PageActionConfig } from '../../contexts/PageActionsContext';
import styles from './unifiedBreadcrumbBar.module.scss';

/**
 * Renders a collapsed "..." breadcrumb item as a clickable chip that
 * opens a dropdown menu with the collapsed items.
 */
const CollapsedBreadcrumb: React.FC<{ item: BreadcrumbItem }> = ({ item }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  return (
    <>
      <Chip
        label="..."
        size="small"
        variant="outlined"
        onClick={handleClick}
        aria-label="Show collapsed breadcrumbs"
        sx={{
          cursor: 'pointer',
          height: 24,
          fontSize: '0.8rem',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {item.collapsedItems?.map((collapsed) => (
          <MenuItem key={collapsed.path} onClick={() => handleItemClick(collapsed.path)}>
            <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
              {collapsed.label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Renders a single page action based on its config type.
 */
const PageAction: React.FC<{ action: PageActionConfig }> = ({ action }) => {
  if (action.type === 'chip') {
    return (
      <Chip
        icon={action.icon ? React.cloneElement(action.icon, { fontSize: 'small' }) : undefined}
        label={action.chipValue ?? action.label}
        size={action.size ?? 'small'}
        variant="outlined"
        onClick={action.onClick ?? undefined}
        disabled={action.disabled}
        sx={!action.onClick ? { cursor: 'default', pointerEvents: 'none' } : undefined}
      />
    );
  }

  if (action.type === 'icon-button') {
    return (
      <IconButton
        onClick={action.onClick}
        size={action.size ?? 'small'}
        disabled={action.disabled}
        aria-label={action.label}
        title={action.label}
      >
        {action.icon}
      </IconButton>
    );
  }

  // type === 'button'
  return (
    <Button
      startIcon={action.icon}
      onClick={action.onClick}
      variant={action.variant ?? 'outlined'}
      color={action.color ?? 'primary'}
      size={action.size ?? 'small'}
      disabled={action.disabled}
    >
      {action.label}
    </Button>
  );
};

/**
 * Help icon button that opens a popover with help sections.
 */
const HelpPopover: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { help } = usePageActions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!help) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: 'info.main' }}
        aria-label="Page help"
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="true"
      >
        <HelpIcon />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'left' : 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'left' : 'center',
        }}
        PaperProps={{
          sx: {
            maxWidth: { xs: 320, sm: 400 },
            p: 3,
            boxShadow: 3,
          },
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {help.title}
          </Typography>
          <Stack spacing={2}>
            {help.sections.map((section, index) => (
              <Box key={index}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {section.content}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Button fullWidth variant="outlined" onClick={() => setAnchorEl(null)} sx={{ mt: 2 }}>
            Got it
          </Button>
        </Box>
      </Popover>
    </>
  );
};

/**
 * Mobile burger menu that shows all page actions + help.
 */
const MobileBurgerMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const { actions, help } = usePageActions();
  const hasContent = actions.length > 0 || help !== null;
  if (!hasContent) return null;

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {help && (
          <MenuItem
            onClick={() => {
              handleClose();
              setHelpOpen(true);
            }}
          >
            <ListItemIcon>
              <HelpIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Help</ListItemText>
          </MenuItem>
        )}
        {actions
          .filter((a) => a.showOnMobile !== false)
          .map((action) => (
            <MenuItem
              key={action.id}
              onClick={() => {
                action.onClick?.();
                handleClose();
              }}
              disabled={action.disabled}
            >
              {action.icon && (
                <ListItemIcon>
                  {React.cloneElement(action.icon, { fontSize: 'small' })}
                </ListItemIcon>
              )}
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
                {action.label}
              </ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Help popover for mobile burger menu */}
      {help && (
        <Popover
          open={helpOpen}
          anchorReference="none"
          onClose={() => setHelpOpen(false)}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          PaperProps={{
            sx: {
              maxWidth: { xs: 320, sm: 400 },
              p: 3,
              boxShadow: 3,
            },
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {help.title}
            </Typography>
            <Stack spacing={2}>
              {help.sections.map((section, index) => (
                <Box key={index}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.content}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Button fullWidth variant="outlined" onClick={() => setHelpOpen(false)} sx={{ mt: 2 }}>
              Got it
            </Button>
          </Box>
        </Popover>
      )}
    </>
  );
};

/**
 * Desktop breadcrumb trail layout.
 */
const DesktopBreadcrumbBar: React.FC = () => {
  const breadcrumbs = useUnifiedBreadcrumbs();
  const { actions, help } = usePageActions();

  if (breadcrumbs.length === 0) return null;

  const lastIndex = breadcrumbs.length - 1;

  return (
    <div className={styles.breadcrumbBar}>
      <div className={styles.breadcrumbTrail}>
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === lastIndex;

          if (crumb.isCollapsed) {
            return (
              <React.Fragment key="collapsed">
                <CollapsedBreadcrumb item={crumb} />
                {!isLast && (
                  <NavigateNextIcon fontSize="small" sx={{ color: 'text.secondary', mx: 0.25 }} />
                )}
              </React.Fragment>
            );
          }

          if (isLast) {
            return (
              <Typography
                key={crumb.path}
                variant="body1"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {crumb.label}
              </Typography>
            );
          }

          return (
            <React.Fragment key={crumb.path}>
              <Typography
                component={RouterLink}
                to={crumb.path}
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                  whiteSpace: 'nowrap',
                }}
              >
                {crumb.label}
              </Typography>
              <NavigateNextIcon fontSize="small" sx={{ color: 'text.secondary', mx: 0.25 }} />
            </React.Fragment>
          );
        })}
      </div>

      {/* Actions area */}
      {(help || actions.length > 0) && (
        <div className={styles.actions}>
          <HelpPopover />
          {actions
            .filter((a) => !a.hideOnDesktop)
            .map((action) => (
              <PageAction key={action.id} action={action} />
            ))}
        </div>
      )}
    </div>
  );
};

/**
 * Mobile breadcrumb bar: back arrow + current page title + burger menu.
 */
const MobileBreadcrumbBar: React.FC = () => {
  const breadcrumbs = useUnifiedBreadcrumbs();
  const navigate = useNavigate();

  if (breadcrumbs.length === 0) return null;

  // Get the flat list of all crumbs (expand collapsed items)
  const flatCrumbs: BreadcrumbItem[] = [];
  for (const crumb of breadcrumbs) {
    if (crumb.isCollapsed && crumb.collapsedItems) {
      flatCrumbs.push(...crumb.collapsedItems);
    } else {
      flatCrumbs.push(crumb);
    }
  }

  const lastCrumb = flatCrumbs[flatCrumbs.length - 1];
  const parentCrumb = flatCrumbs.length > 1 ? flatCrumbs[flatCrumbs.length - 2] : null;
  const showBackButton = flatCrumbs.length > 1;

  return (
    <div className={styles.mobileBreadcrumbBar}>
      {showBackButton && parentCrumb && (
        <IconButton
          edge="start"
          onClick={() => navigate(parentCrumb.path)}
          size="small"
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}

      <Typography
        variant="subtitle1"
        component="h1"
        className={styles.mobileTitle}
        sx={{ fontWeight: 600 }}
      >
        {lastCrumb.label}
      </Typography>

      <MobileBurgerMenu />
    </div>
  );
};

/**
 * Unified breadcrumb bar that renders desktop or mobile layout based on screen size.
 */
export const UnifiedBreadcrumbBar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return <MobileBreadcrumbBar />;
  }

  return <DesktopBreadcrumbBar />;
};
